import React, { useMemo } from 'react'

import { isTeamExternalIntegrationAzure } from 'components/common/mapped-entities/azure/authentication/azure-integration-utils'
import { AzureNewAuthenticationForm } from 'components/common/mapped-entities/azure/authentication/AzureNewAuthenticationForm'
import PageSection from 'components/common/PageSection'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useGetAzureMetadataAuthorization } from 'queries/azure/use-get-azure-metadata-authorization'
import { messageFromResponseError } from 'utils/strings'
import { type OnSuccessExternalIntegration } from 'utils/team-external-integrations/team-external-integrations-util'

export function AzureNewAuthenticationContainer({
  isEnabled,
  onSuccess
}: {
  isEnabled: boolean
  onSuccess?: OnSuccessExternalIntegration
}) {
  const {
    externalIntegrationsError,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    teamExternalIntegrations
  } = useStatusCheckExternalIntegrationsContext()

  const {
    data: azureMetadataAuthorizationData,
    error: errorAzureMetadataAuthorization,
    isError: isErrorAzureMetadataAuthorization,
    isLoading: isLoadingAzureMetadataAuthorization
  } = useGetAzureMetadataAuthorization({ enabled: isEnabled })

  const azureExternalIntegrations = useMemo(() => {
    if (!isEnabled || !teamExternalIntegrations) {
      return []
    }

    return teamExternalIntegrations.filter(isTeamExternalIntegrationAzure)
  }, [isEnabled, teamExternalIntegrations])

  const isLoading =
    isFetchingExternalIntegrations || isLoadingAzureMetadataAuthorization
  const hasError =
    hasErrorExternalIntegrations || isErrorAzureMetadataAuthorization
  const errorMessage = hasErrorExternalIntegrations
    ? messageFromResponseError(
        externalIntegrationsError,
        'There was an issue while retrieving External Integrations'
      )
    : messageFromResponseError(
        errorAzureMetadataAuthorization,
        'Failed to fetch Azure authentication metadata'
      )

  return (
    <PageSection
      css={{ alignItems: 'start' }}
      errorMessage={errorMessage}
      hasError={hasError}
      isLoading={isLoading}
      isLoadingOverlayFullScreen={false}
    >
      {() => {
        if (!azureMetadataAuthorizationData || !teamExternalIntegrations) {
          throw new Error('There was an error loading the page')
        }

        return (
          <AzureNewAuthenticationForm
            azureExternalIntegrations={azureExternalIntegrations}
            templateUrl={azureMetadataAuthorizationData.deploymentTemplateUrl}
            onSuccess={onSuccess}
          />
        )
      }}
    </PageSection>
  )
}
